import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import {
  ArrowLeft,
  BadgeCheck,
  ChevronDown,
  ChevronUp,
  EyeOff,
  Eye,
  ExternalLink,
  ImageOff,
  MessageSquare,
  Play,
  Trash2,
  UserX,
  X,
} from '@digital-twins/design-system/icons'
import {
  SocialBrand,
  SocialPostComment,
  SocialPostEngagement,
  SocialPostImage,
  SocialPostItem,
  SocialTopic,
  SocialTopicPostItem,
} from '@digital-twins/shared-types'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { intlLocaleForUi } from '@/i18n'
import { cn } from '@/lib/utils'
import { SocialBrandMark } from './SocialBrandMark'
import { SocialPlatformIcon } from './SocialPlatformIcon'
import {
  SOCIAL_PLAY_BUTTON_CLASS,
  SOCIAL_PLAYER_CLOSE_CLASS,
  socialPlayerUrl,
  useSocialPlayingPost,
} from './SocialPlayingPostContext'
import {
  formatSocialMeasure,
  socialBrandSwatchClass,
  SocialAuthorAvatar,
  SocialSentimentPill,
  SocialSourcePill,
} from './SocialPostTile'
import {
  EMBED_SIZE_CLASS,
  formatSocialCount,
  formatSocialDate,
  SOCIAL_PLATFORM_LABELS,
  socialMetricLabelKey,
} from './socialFormat'
import { registerTopicHotkey } from './useTopicHotkeys'
import { useSocialItemImage } from './useSocialItemImage'
import { useSocialListeningApi } from './SocialListeningApiContext'

/** The Topic a post is read in: its name, themes and brands name the post's tags. */
export interface SocialPostTopicContext {
  name: string
  topics: Array<SocialTopic>
  brands: Array<SocialBrand>
}

interface SocialPostDetailProps {
  post: SocialPostItem
  canManage: boolean
  onBack: () => void
  onRemovePost: (post: SocialPostItem) => void
  onRemoveAuthor: (handle: string) => void
  /** Set in the Topic view: shows themes, brands, sentiment and "Hide from <Topic>". */
  topic?: SocialPostTopicContext | null
  /** False where hiding is not offered (the back office). Defaults to `canManage`. */
  canHide?: boolean
  onHide?: (post: SocialPostItem) => void
  onUnhide?: (post: SocialPostItem) => void
  /** `j` / `k` and the arrows: the next (1) or previous (-1) post of the boards; absent hides them. */
  onStep?: (direction: 1 | -1) => void
  /** "Ask about this post": opens the Assistant 2.0 entry; absent hides it. */
  onAsk?: (post: SocialPostItem) => void
}

const ENGAGEMENT_KEYS: Array<keyof SocialPostEngagement> = ['likes', 'comments', 'shares', 'views']

/** The sheet's metrics grid: reach first. */
const SHEET_METRIC_KEYS: Array<keyof SocialPostEngagement> = ['views', 'likes', 'comments', 'shares']

interface SocialStoredImageProps {
  image: SocialPostImage
  alt: string
  className?: string
  testId: string
}

/** One stored image through the authenticated proxy, with a quiet placeholder on failure. */
function SocialStoredImage(props: SocialStoredImageProps) {
  const { image, alt, className, testId } = props

  const { t } = useTranslation('app')
  const { src, isLoading, hasError } = useSocialItemImage(image.imagePath)

  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        decoding="async"
        className={cn('rounded-xl border border-card-border bg-background object-cover', className)}
        data-testid={testId}
      />
    )
  }
  if (isLoading) {
    return (
      <span
        className={cn(
          'block animate-pulse rounded-xl border border-card-border bg-foreground/[0.04] motion-reduce:animate-none',
          className,
        )}
        aria-hidden="true"
      />
    )
  }
  return (
    <span
      className={cn(
        'flex items-center justify-center rounded-xl border border-card-border bg-primary/5 text-muted-foreground',
        className,
      )}
      role="img"
      aria-label={hasError ? t('socialListening.topicPost.imageFailed') : alt}
      data-testid={`${testId}-failed`}
    >
      <ImageOff className="h-4 w-4" aria-hidden="true" />
    </span>
  )
}

interface SocialPostImageGroups {
  cover: SocialPostImage | null
  stills: Array<SocialPostImage>
  photos: Array<SocialPostImage>
}

/**
 * The cover, the video stills and the other photos of a post. Frames only
 * ever show in the stills strip: when the cover failed to store, the first
 * photo stands in, never a frame.
 */
export function socialPostImageGroups(images: Array<SocialPostImage>): SocialPostImageGroups {
  const cover =
    images.find((image) => image.kind === 'cover') ??
    images.find((image) => image.kind !== 'frame') ??
    null
  return {
    cover,
    stills: images.filter((image) => image.kind === 'frame'),
    photos: images.filter((image) => image.kind !== 'frame' && image !== cover),
  }
}

/** `HH:MM` of an ISO instant in UTC, or null. */
function utcTime(iso: string | null, uiLanguage: string): string | null {
  if (!iso) {
    return null
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return new Intl.DateTimeFormat(intlLocaleForUi(uiLanguage), {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'UTC',
  }).format(date)
}

/** The day of an ISO instant in UTC, as the boards and the chart count it. */
function utcDate(iso: string | null, uiLanguage: string): string | null {
  if (!iso) {
    return null
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return new Intl.DateTimeFormat(intlLocaleForUi(uiLanguage), {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

/** Registers `j` / `k` for the sheet while `onStep` is given. */
function useStepHotkeys(onStep: ((direction: 1 | -1) => void) | undefined) {
  useEffect(() => {
    if (!onStep) {
      return undefined
    }
    const offNext = registerTopicHotkey('j', () => onStep(1))
    const offPrevious = registerTopicHotkey('k', () => onStep(-1))
    return () => {
      offNext()
      offPrevious()
    }
  }, [onStep])
}

type SocialCommentsState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'loaded'; comments: Array<SocialPostComment> }

/**
 * The kept comments under a post (D13), read once when the post was found.
 * `commentsCollected` null: its comments were not read, said plainly ("not
 * collected", never 0). Absent (a server before collection v2): nothing.
 */
function SocialPostComments(props: { post: SocialPostItem }) {
  const { post } = props

  const { t, i18n } = useTranslation('app')
  const api = useSocialListeningApi()
  const collected = (post as Partial<SocialTopicPostItem>).commentsCollected
  const [state, setState] = useState<SocialCommentsState>({ status: 'loading' })

  useEffect(() => {
    if (collected == null || collected === 0) {
      return undefined
    }
    let cancelled = false
    setState({ status: 'loading' })
    api
      .listPostComments(post.token)
      .then((response) => {
        if (!cancelled) {
          setState({ status: 'loaded', comments: response.comments })
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ status: 'failed' })
        }
      })
    return () => {
      cancelled = true
    }
  }, [api, collected, post.token])

  if (collected === undefined) {
    return null
  }
  const testId = `social-post-comments-${post.token}`
  if (collected === null) {
    return (
      <section className="space-y-2" data-testid={testId}>
        <h3 className="text-[13px] font-semibold text-foreground">
          {t('socialListening.topicPost.comments.title')}
        </h3>
        <Text as="p" size="xs" color="muted" data-testid={`${testId}-not-collected`}>
          {t('socialListening.topicPost.comments.notCollected')}
        </Text>
      </section>
    )
  }
  const body =
    collected === 0 || (state.status === 'loaded' && state.comments.length === 0) ? (
      <Text as="p" size="xs" color="muted" data-testid={`${testId}-empty`}>
        {t('socialListening.topicPost.comments.empty')}
      </Text>
    ) : state.status === 'loading' ? (
      <Text as="p" size="xs" color="muted">
        {t('socialListening.topicPost.comments.loading')}
      </Text>
    ) : state.status === 'failed' ? (
      <Text as="p" size="xs" color="muted" data-testid={`${testId}-failed`}>
        {t('socialListening.topicPost.comments.failed')}
      </Text>
    ) : (
      <ul className="space-y-3" data-testid={`${testId}-list`}>
        {state.comments.map((comment) => {
          const day = utcDate(comment.publishedAt, i18n.language)
          const likes = comment.engagement.likes
          return (
            <li
              key={comment.token}
              className="rounded-[var(--radius)] border border-card-border px-3 py-2.5"
              data-testid={`social-post-comment-${comment.token}`}
            >
              <div className="flex flex-wrap items-center gap-x-2 text-[12px] text-muted-foreground">
                <span className="font-medium text-foreground">
                  {comment.authorHandle
                    ? `@${comment.authorHandle.replace(/^@+/, '')}`
                    : t('socialListening.topicPost.comments.unknownAuthor')}
                </span>
                <span>{day ?? t('socialListening.topicPost.comments.undated')}</span>
                {likes != null ? (
                  <span className="tabular-nums">
                    {t('socialListening.topicPost.comments.likes', {
                      count: likes,
                      value: formatSocialMeasure(likes, i18n.language),
                    })}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 whitespace-pre-line text-[14px] leading-[1.5] text-foreground">
                {comment.text}
              </p>
            </li>
          )
        })}
      </ul>
    )
  return (
    <section className="space-y-2" data-testid={testId}>
      <h3 className="text-[13px] font-semibold text-foreground">
        {t('socialListening.topicPost.comments.title')}
        {collected > 0 ? (
          <span className="ml-1.5 font-normal tabular-nums text-muted-foreground">
            {formatSocialMeasure(collected, i18n.language)}
          </span>
        ) : null}
      </h3>
      {body}
    </section>
  )
}

interface SheetChipProps {
  children: ReactNode
  title?: string
}

function SheetChip(props: SheetChipProps) {
  const { children, title } = props

  return (
    <span
      className="inline-flex h-6 max-w-full items-center gap-1.5 rounded-full border border-border px-2.5 text-[12px] text-foreground"
      title={title}
    >
      {children}
    </span>
  )
}

interface SocialPostPlayButtonProps {
  postToken: string
  onPlay: () => void
}

/** The pane's Play: mounts the platform's player in place of the cover. */
function SocialPostPlayButton(props: SocialPostPlayButtonProps) {
  const { postToken, onPlay } = props

  const { t } = useTranslation('app')

  return (
    <button
      type="button"
      aria-label={t('socialListening.topicPage.tile.play')}
      title={t('socialListening.topicPage.tile.play')}
      className={cn(SOCIAL_PLAY_BUTTON_CLASS, 'h-11 w-11')}
      onClick={onPlay}
      data-testid={`social-post-play-${postToken}`}
    >
      <Play className="ml-0.5 h-4 w-4 fill-current" aria-hidden="true" />
    </button>
  )
}

interface SocialPostPlayerProps {
  postToken: string
  src: string
  title: string
  className: string
  sizeClass: string
  onClose: () => void
}

/** The pane's mounted player, with the same Close as a tile: back to the cover. */
function SocialPostPlayer(props: SocialPostPlayerProps) {
  const { postToken, src, title, className, sizeClass, onClose } = props

  const { t } = useTranslation('app')

  return (
    // The frame's own size, so Close sits on the player's corner.
    <div className={cn('relative mx-auto w-full', sizeClass)}>
      <iframe
        src={src}
        title={title}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        className={className}
        data-testid={`social-post-embed-${postToken}`}
      />
      <button
        type="button"
        aria-label={t('socialListening.topicPage.tile.closePlayer')}
        title={t('socialListening.topicPage.tile.closePlayer')}
        className={SOCIAL_PLAYER_CLOSE_CLASS}
        onClick={onClose}
        data-testid={`social-post-close-${postToken}`}
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  )
}

interface SocialPostShowButtonProps {
  postToken: string
  onShow: () => void
}

/** Loads the embed of a post without video or stored cover; it never autoplays a video. */
function SocialPostShowButton(props: SocialPostShowButtonProps) {
  const { postToken, onShow } = props

  const { t } = useTranslation('app')

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      shape="pill"
      className="absolute left-1/2 top-[42%] z-[2] h-9 -translate-x-1/2 -translate-y-1/2 bg-background/90 px-4 backdrop-blur-md"
      onClick={onShow}
      data-testid={`social-post-show-${postToken}`}
    >
      {t('socialListening.topicPage.tile.showPost')}
    </Button>
  )
}

export function SocialPostDetail(props: SocialPostDetailProps) {
  const {
    post,
    canManage,
    onBack,
    onRemovePost,
    onRemoveAuthor,
    topic = null,
    canHide = canManage,
    onHide,
    onUnhide,
    onStep,
    onAsk,
  } = props

  const { t, i18n } = useTranslation('app')
  // The pane opens on the cover; the player mounts only on Play, and it
  // shares the boards' one-at-a-time state (plan D3).
  const player = useSocialPlayingPost(post.token, 'pane')
  const stopPlayer = player.stop

  useStepHotkeys(topic ? onStep : undefined)

  // Up and down (or any other post) start again on the cover.
  useEffect(() => stopPlayer, [stopPlayer])

  const author = post.authorHandle
    ? `@${post.authorHandle}`
    : t('socialListening.detail.unknownAuthor')
  const platformLabel = SOCIAL_PLATFORM_LABELS[post.platform]
  const date = formatSocialDate(post.publishedAt ?? post.collectedAt, i18n.language)
  const followers = post.providerMeta?.followers ?? null
  const isVerified = post.providerMeta?.verified === true
  const images = post.images ?? []
  const { cover, stills, photos } = socialPostImageGroups(images)
  const embedUrl = socialPlayerUrl(post)
  // Videos get a Play button on their cover; a post with neither video nor
  // stored cover (a photo or a Reddit thread) loads its embed on a click.
  const playerUrl = post.media.hasVideo ? embedUrl : null
  const showPostUrl = !post.media.hasVideo && !cover ? embedUrl : null
  const mountedUrl = playerUrl ?? showPostUrl
  const isPlaying = mountedUrl != null && player.isPlaying
  const onPlay = player.play
  const engagement = ENGAGEMENT_KEYS.flatMap((key) => {
    const value = post.engagement[key]
    if (value == null) {
      return []
    }
    return [
      t(`socialListening.detail.engagement.${socialMetricLabelKey(post.platform, key)}`, {
        count: value,
        value: formatSocialCount(value, i18n.language),
      }),
    ]
  })
  const followersLabel =
    followers != null
      ? t('socialListening.topicPost.followers', {
          count: followers,
          value: formatSocialMeasure(followers, i18n.language),
        })
      : null

  const themeNames = topic
    ? post.topicIds.flatMap((id) => topic.topics.find((item) => item.id === id)?.name ?? [])
    : []
  // `seen` does not say which image showed the brand: with video frames stored
  // it may be a frame or the cover, without them it can only be an image.
  const seenSuffix = stills.length > 0
    ? t('socialListening.topicPost.seenInImageOrVideo')
    : t('socialListening.topicPost.seenInPicture')
  const brandTags = topic
    ? (post.brands ?? []).flatMap((tag) => {
        const index = topic.brands.findIndex((item) => item.id === tag.id)
        if (index < 0) {
          return []
        }
        const brand = topic.brands[index]
        const base = tag.sentiment
          ? t(`socialListening.topicPost.brandWithSentiment.${tag.sentiment}`, {
              brand: brand.name,
            })
          : brand.name
        return [
          {
            id: brand.id,
            name: brand.name,
            logoPath: brand.logoPath ?? null,
            isSeen: tag.seen === true,
            index,
            description: tag.seen ? `${base} ${seenSuffix}` : base,
          },
        ]
      })
    : []
  const hasNoJevTags = post.judgedBy === 'openai'
  const isHidden = post.hidden === true

  const removeAuthorButton = post.authorHandle ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-8 px-2 text-[12px] text-muted-foreground"
      data-testid={`social-post-remove-author-${post.token}`}
      onClick={() => onRemoveAuthor(post.authorHandle as string)}
    >
      <UserX className="mr-1 h-3.5 w-3.5" />
      {t('socialListening.detail.removeAuthor')}
    </Button>
  ) : null

  const legacyActions = canManage ? (
    <div className="ml-auto flex items-center gap-1">
      {removeAuthorButton}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 px-2 text-[12px] text-destructive hover:text-destructive"
        data-testid={`social-post-remove-${post.token}`}
        onClick={() => onRemovePost(post)}
      >
        <Trash2 className="mr-1 h-3.5 w-3.5" />
        {t('socialListening.detail.removePost')}
      </Button>
    </div>
  ) : null

  if (!topic) {
    // The tracker drawer: the post inline, with a back link and the original.
    return (
      <article className="space-y-5" data-testid={`social-post-detail-${post.token}`}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="-ml-2 h-8 px-2 text-[13px] text-muted-foreground"
          data-testid="social-post-detail-back"
          onClick={onBack}
        >
          <ArrowLeft className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          {t('socialListening.detail.backToPosts')}
        </Button>

        <header className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-card-border bg-background">
            <SocialPlatformIcon platform={post.platform} className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1 truncate text-[15px] font-medium text-foreground">
              {post.providerMeta?.displayName ?? author}
              {isVerified ? (
                <BadgeCheck
                  className="h-4 w-4 shrink-0 text-primary"
                  aria-label={t('socialListening.topicPost.verified')}
                />
              ) : null}
            </div>
            <div
              className="text-[12px] text-muted-foreground"
              data-testid={`social-post-meta-${post.token}`}
            >
              {`${platformLabel}${date ? ` · ${date}` : ''}`}
            </div>
          </div>
        </header>

        {isPlaying && mountedUrl ? (
          <SocialPostPlayer
            postToken={post.token}
            src={mountedUrl}
            title={t('socialListening.detail.previewTitle', { author })}
            className={`mx-auto block w-full rounded-xl border border-border bg-background ${EMBED_SIZE_CLASS[post.platform]}`}
            sizeClass={EMBED_SIZE_CLASS[post.platform]}
            onClose={stopPlayer}
          />
        ) : cover ? (
          <div className="relative mx-auto w-full max-w-[420px]">
            <SocialStoredImage
              image={cover}
              alt={t('socialListening.topicPost.coverAlt')}
              className="block max-h-[480px] w-full"
              testId={`social-post-cover-${post.token}`}
            />
            {playerUrl ? <SocialPostPlayButton postToken={post.token} onPlay={onPlay} /> : null}
          </div>
        ) : playerUrl ? (
          <div
            className="relative mx-auto aspect-video w-full max-w-[420px] rounded-xl border border-card-border bg-[linear-gradient(165deg,hsl(var(--primary)/0.16)_0%,hsl(var(--background))_80%)]"
            data-testid={`social-post-video-frame-${post.token}`}
          >
            <SocialPostPlayButton postToken={post.token} onPlay={onPlay} />
          </div>
        ) : showPostUrl ? (
          <div
            className="relative mx-auto flex aspect-video w-full max-w-[420px] items-center justify-center rounded-xl border border-card-border bg-[linear-gradient(165deg,hsl(var(--primary)/0.16)_0%,hsl(var(--background))_80%)]"
            data-testid={`social-post-placeholder-${post.token}`}
          >
            <SocialPostShowButton postToken={post.token} onShow={onPlay} />
          </div>
        ) : null}

        <Text size="sm" className="whitespace-pre-line text-[14px] leading-relaxed">
          {post.text}
        </Text>

        {engagement.length > 0 ? (
          <Text as="p" size="xs" color="muted">
            {engagement.join(' · ')}
          </Text>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
          <Button asChild variant="outline" size="sm" shape="pill" className="h-9 px-4">
            <a
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`social-post-link-${post.token}`}
            >
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              {t('socialListening.detail.openOriginal')}
            </a>
          </Button>
          {legacyActions}
        </div>
      </article>
    )
  }

  // The Topic page: the prototype's post sheet (concept §5.10).
  const name = post.providerMeta?.displayName ?? (post.authorHandle ? author : null)
  const headerName = name ?? t('socialListening.topicPage.sheet.unknownAuthor')
  const headerSub = [
    ...(post.authorHandle ? [author] : []),
    ...(followersLabel ? [followersLabel] : []),
  ].join(', ')
  const publishedIso = post.publishedAt ?? post.collectedAt
  const publishedDate = utcDate(publishedIso, i18n.language)
  const publishedTime = utcTime(publishedIso, i18n.language)
  const published =
    publishedDate && publishedTime
      ? t('socialListening.topicPage.sheet.published', { date: publishedDate, time: publishedTime })
      : null
  const hasMediaSource = images.length > 0 || Boolean(post.media.thumbnailUrl)
  const iconButtonClass =
    'flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-foreground transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

  const media = isPlaying && mountedUrl ? (
    <SocialPostPlayer
      postToken={post.token}
      src={mountedUrl}
      title={t('socialListening.detail.previewTitle', { author })}
      className={`mx-auto block w-full rounded-xl border border-card-border bg-background ${EMBED_SIZE_CLASS[post.platform]}`}
      sizeClass={EMBED_SIZE_CLASS[post.platform]}
      onClose={stopPlayer}
    />
  ) : cover ? (
    <div className="relative">
      <SocialStoredImage
        image={cover}
        alt={t('socialListening.topicPost.coverAlt')}
        className="block aspect-square w-full"
        testId={`social-post-cover-${post.token}`}
      />
      <span className="absolute left-2.5 top-2.5 inline-flex h-6 items-center gap-1.5 rounded-full bg-background/90 px-2 text-[12px] font-medium text-foreground shadow-sm backdrop-blur-md">
        <SocialPlatformIcon platform={post.platform} className="h-3 w-3" />
        {platformLabel}
      </span>
      {playerUrl ? <SocialPostPlayButton postToken={post.token} onPlay={onPlay} /> : null}
    </div>
  ) : hasMediaSource || mountedUrl ? (
    <div
      className="relative flex aspect-square flex-col justify-end overflow-hidden rounded-[calc(var(--radius)+4px)] border border-card-border bg-[linear-gradient(165deg,hsl(var(--primary)/0.16)_0%,hsl(var(--background))_80%)] p-4"
      data-testid={`social-post-placeholder-${post.token}`}
    >
      <span className="absolute left-2.5 top-2.5 inline-flex h-6 items-center gap-1.5 rounded-full bg-background/90 px-2 text-[12px] font-medium text-foreground shadow-sm backdrop-blur-md">
        <SocialPlatformIcon platform={post.platform} className="h-3 w-3" />
        {platformLabel}
      </span>
      {playerUrl ? (
        <SocialPostPlayButton postToken={post.token} onPlay={onPlay} />
      ) : showPostUrl ? (
        <SocialPostShowButton postToken={post.token} onShow={onPlay} />
      ) : post.media.hasVideo ? (
        <span className="absolute left-1/2 top-[42%] flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-foreground/15 bg-background/70">
          <Play className="ml-0.5 h-4 w-4 fill-current" aria-hidden="true" />
        </span>
      ) : null}
      <span className="line-clamp-4 font-display text-[22px] leading-[1.25] text-foreground">
        {post.text.slice(0, 120)}
      </span>
    </div>
  ) : null

  const hideButton =
    canHide && (isHidden ? onUnhide : onHide) ? (
      <Button
        type="button"
        variant="outline"
        size="sm"
        shape="pill"
        className="h-9 px-4"
        data-testid={`social-post-${isHidden ? 'unhide' : 'hide'}-${post.token}`}
        onClick={() => (isHidden ? onUnhide?.(post) : onHide?.(post))}
      >
        {isHidden ? (
          <Eye className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <EyeOff className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
        )}
        {isHidden
          ? t('socialListening.topicPost.unhide', { name: topic.name })
          : t('socialListening.topicPost.hide', { name: topic.name })}
      </Button>
    ) : null

  const reportMenu = canManage ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-9 px-3 text-[13px] text-muted-foreground"
          data-testid={`social-post-report-${post.token}`}
        >
          {t('socialListening.topicPost.reportOrDelete')}
          <ChevronDown className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px] rounded-xl">
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          data-testid={`social-post-remove-${post.token}`}
          onClick={() => onRemovePost(post)}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {t('socialListening.topicPost.removePost')}
        </DropdownMenuItem>
        {post.authorHandle ? (
          <DropdownMenuItem
            data-testid={`social-post-remove-author-${post.token}`}
            onClick={() => onRemoveAuthor(post.authorHandle as string)}
          >
            <UserX className="mr-2 h-4 w-4" />
            {t('socialListening.topicPost.removeAuthor')}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null

  return (
    <article
      className="flex h-full min-h-0 flex-col bg-background"
      aria-label={t('socialListening.topicPage.sheet.ariaLabel')}
      data-testid={`social-post-detail-${post.token}`}
    >
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <SocialAuthorAvatar
            name={headerName}
            platform={post.platform}
            avatarPath={post.authorAvatarPath}
            hasMark
            size="xs"
            testId={`social-post-detail-avatar-img-${post.token}`}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-1 truncate text-[14px] font-semibold text-foreground">
              <span className="truncate">{headerName}</span>
              {isVerified ? (
                <BadgeCheck
                  className="h-4 w-4 shrink-0 text-primary"
                  aria-label={t('socialListening.topicPost.verified')}
                />
              ) : null}
            </div>
            {headerSub ? (
              <div
                className="truncate text-[12px] tabular-nums text-muted-foreground"
                data-testid={`social-post-meta-${post.token}`}
              >
                {headerSub}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {onStep ? (
            <>
              <button
                type="button"
                className={iconButtonClass}
                aria-label={`${t('socialListening.topicPage.sheet.previous')} (k)`}
                title={`${t('socialListening.topicPage.sheet.previous')} (k)`}
                data-testid="social-post-detail-previous"
                onClick={() => onStep(-1)}
              >
                <ChevronUp className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                className={iconButtonClass}
                aria-label={`${t('socialListening.topicPage.sheet.next')} (j)`}
                title={`${t('socialListening.topicPage.sheet.next')} (j)`}
                data-testid="social-post-detail-next"
                onClick={() => onStep(1)}
              >
                <ChevronDown className="h-4 w-4" aria-hidden="true" />
              </button>
            </>
          ) : null}
          <button
            type="button"
            className={iconButtonClass}
            aria-label={t('socialListening.topicPage.sheet.close')}
            data-testid="social-post-detail-close"
            onClick={onBack}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-5 pb-8 pt-4">
        {isHidden ? (
          <Text
            as="p"
            size="xs"
            color="muted"
            className="rounded-lg border border-card-border px-3 py-2"
            data-testid={`social-post-hidden-${post.token}`}
          >
            {t('socialListening.topicPost.hiddenNote', { name: topic.name })}
          </Text>
        ) : null}

        {media}

        <div className="flex flex-wrap items-center gap-2">
          {post.sentiment ? (
            <SocialSentimentPill
              sentiment={post.sentiment}
              testId={`social-post-sentiment-label-${post.token}`}
            />
          ) : null}
          <SocialSourcePill
            post={post}
            testId={`social-post-source-label-${post.token}`}
          />
          {published ? (
            <span className="text-[13px] tabular-nums text-muted-foreground">{published}</span>
          ) : null}
        </div>

        <p className="whitespace-pre-line text-[16px] leading-[1.6] text-foreground">{post.text}</p>

        <dl
          className="grid grid-cols-4 rounded-[var(--radius)] border border-card-border"
          data-testid={`social-post-metrics-${post.token}`}
        >
          {SHEET_METRIC_KEYS.map((key, index) => {
            const value = post.engagement[key]
            return (
              <div
                key={key}
                className={cn('flex flex-col-reverse px-3 py-2.5', index > 0 && 'border-l border-card-border')}
              >
                <dt className="text-[12px] text-muted-foreground">
                  {t(`socialListening.topicPage.sheet.${socialMetricLabelKey(post.platform, key)}`)}
                </dt>
                <dd className="text-[18px] font-semibold tabular-nums text-foreground">
                  {value != null
                    ? formatSocialMeasure(value, i18n.language)
                    : t('socialListening.topicPage.stats.notAvailable')}
                </dd>
              </div>
            )
          })}
        </dl>

        {stills.length > 0 ? (
          <section className="space-y-2" data-testid={`social-post-stills-${post.token}`}>
            <ul className="grid grid-cols-4 gap-2">
              {stills.map((image, position) => (
                <li key={image.index}>
                  <SocialStoredImage
                    image={image}
                    alt={t('socialListening.topicPost.stillAlt', { index: position + 1 })}
                    className="aspect-[9/16] w-full"
                    testId={`social-post-still-${post.token}-${position}`}
                  />
                </li>
              ))}
            </ul>
            <Text as="p" size="xs" color="muted">
              {t('socialListening.topicPost.stills')}
            </Text>
          </section>
        ) : null}

        {photos.length > 0 ? (
          <ul className="grid grid-cols-4 gap-2" data-testid={`social-post-photos-${post.token}`}>
            {photos.map((image, position) => (
              <li key={image.index}>
                <SocialStoredImage
                  image={image}
                  alt={t('socialListening.topicPost.photoAlt', { index: position + 2 })}
                  className="aspect-square w-full"
                  testId={`social-post-photo-${post.token}-${position}`}
                />
              </li>
            ))}
          </ul>
        ) : null}

        <dl
          className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3 gap-y-3 text-[13px]"
          data-testid={`social-post-tags-${post.token}`}
        >
          <dt className="pt-0.5 text-muted-foreground">{t('socialListening.topicPost.themes')}</dt>
          <dd className="flex flex-wrap gap-1.5" data-testid={`social-post-themes-${post.token}`}>
            {themeNames.length > 0 ? (
              themeNames.map((themeName) => <SheetChip key={themeName}>{themeName}</SheetChip>)
            ) : (
              <SheetChip>{t('socialListening.topicView.other')}</SheetChip>
            )}
          </dd>
          <dt className="pt-0.5 text-muted-foreground">{t('socialListening.topicPost.brands')}</dt>
          <dd className="flex flex-wrap gap-1.5" data-testid={`social-post-brands-${post.token}`}>
            {brandTags.length > 0 ? (
              brandTags.map((tag) => (
                <SheetChip key={tag.id} title={tag.description}>
                  {tag.isSeen ? (
                    <Eye className="h-3 w-3 shrink-0" aria-hidden="true" />
                  ) : (
                    <SocialBrandMark
                      logoPath={tag.logoPath}
                      swatch={
                        <span
                          className={cn('h-2 w-2 shrink-0 rounded-sm', socialBrandSwatchClass(tag.index))}
                          aria-hidden="true"
                        />
                      }
                      testId={`social-post-brands-mark-${post.token}-${tag.index}`}
                    />
                  )}
                  <span className="truncate">
                    {tag.isSeen
                      ? t('socialListening.topicPage.sheet.brandSeen', { brand: tag.name })
                      : tag.name}
                  </span>
                  <span className="sr-only">{tag.description}</span>
                </SheetChip>
              ))
            ) : (
              <span className="text-foreground">{t('socialListening.topicPost.none')}</span>
            )}
          </dd>
          {hasNoJevTags ? (
            <dd className="col-span-2 text-[12px] text-muted-foreground">
              {t('socialListening.topicPost.noTagsYet')}
            </dd>
          ) : null}
        </dl>

        <SocialPostComments post={post} />

        <div className="flex flex-wrap items-center gap-2 pt-2">
          <Button asChild variant="outline" size="sm" shape="pill" className="h-9 px-4">
            <a
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`social-post-open-on-${post.token}`}
            >
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              {t('socialListening.topicPage.sheet.openOriginal')}
            </a>
          </Button>
          {onAsk ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              shape="pill"
              className="h-9 px-4"
              data-testid={`social-post-ask-${post.token}`}
              onClick={() => onAsk(post)}
            >
              <MessageSquare className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              {t('socialListening.topicPage.sheet.ask')}
            </Button>
          ) : null}
          {hideButton}
          {reportMenu ? <div className="ml-auto">{reportMenu}</div> : null}
        </div>
      </div>
    </article>
  )
}

interface SocialPostSheetProps extends Omit<SocialPostDetailProps, 'post' | 'onBack' | 'topic'> {
  /** The open post; null closes the sheet. */
  post: SocialPostItem | null
  topic: SocialPostTopicContext
  onClose: () => void
}

/**
 * The Topic page's post sheet: 480 px from the right over a light scrim,
 * `j` / `k` to step, Esc or the close button to leave. The page keeps
 * scrolling state behind it.
 */
export function SocialPostSheet(props: SocialPostSheetProps) {
  const { post, topic, onClose, ...detailProps } = props

  const { t } = useTranslation('app')

  return (
    <DialogPrimitive.Root
      open={post != null}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose()
        }
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-foreground/25 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 motion-reduce:animate-none" />
        <DialogPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[480px] flex-col border-l border-border bg-background shadow-lg duration-[420ms] ease-out data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:animate-in data-[state=open]:slide-in-from-right focus:outline-none motion-reduce:animate-none"
          aria-describedby={undefined}
          data-testid="social-post-sheet"
        >
          <DialogPrimitive.Title className="sr-only">
            {t('socialListening.topicPage.sheet.ariaLabel')}
          </DialogPrimitive.Title>
          {post ? (
            <SocialPostDetail {...detailProps} post={post} topic={topic} onBack={onClose} />
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
